import { formatNumberWithCommas } from '@utils/numberFormatters';

export const formatValueWithResolution = (
  value: number | null,
  resolution: number
): string => {
  if (value === null) return '';

  const normalizedValue = Object.is(value, -0) || value === 0 ? 0 : value;

  if (normalizedValue === 0 && resolution === 0) {
    return '0';
  }

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: resolution,
    maximumFractionDigits: resolution
  }).format(normalizedValue);

  // Final check: if the formatted string is "-0.000" or similar, fix it
  if (formatted.startsWith('-0') && parseFloat(formatted) === 0) {
    return formatted.substring(1); // Remove the negative sign
  }

  return formatted;
};

export const formatValue = (
  value: number | null,
  readability: number
): string => {
  if (value === null) return '';

  const normalizedValue = Object.is(value, -0) || value === 0 ? 0 : value;

  const decimalPlaces =
    readability < 1 ? Math.abs(Math.floor(Math.log10(readability))) : 0;

  const formatted = normalizedValue.toFixed(decimalPlaces);

  // Additional check - if the formatted string is "-0.00" or similar, fix it
  if (formatted.startsWith('-0') && parseFloat(formatted) === 0) {
    return formatted.substring(1); // Remove the negative sign
  }

  const [integerPart, decimalPart] = formatted.split('.');

  const formattedInteger = parseInt(integerPart).toLocaleString('en-US');

  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

export const formatInputValue = (
  value: string,
  readability: number,
  onBlur: boolean = false
): string => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return onBlur ? '' : value;

  if (onBlur) {
    return formatValue(numValue, readability);
  }
  return value;
};

export const formatInputValueWithResolution = (
  value: string,
  resolution: number,
  _readability: number,
  onBlur: boolean = false
): string => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return onBlur ? '' : value;

  if (onBlur) {
    return formatValueWithResolution(numValue, resolution);
  }
  return value;
};

export const parseInputValue = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = parseFloat(trimmed);
  return isNaN(parsed) ? null : parsed;
};

export const formatDisplayValue = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined || value === '') return '';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return String(value);

  return formatNumberWithCommas(numValue);
};

export const parseDisplayValue = (displayValue: string): string => {
  return displayValue;
};

export const getPassFailLabel = (
  error: number | null,
  hysteresis: number | null,
  tolerance: number
): string => {
  if (error === null) return '';

  const absoluteError = Math.abs(error) <= tolerance;
  const hysteresisPasses = hysteresis === null || hysteresis <= tolerance;

  return absoluteError && hysteresisPasses ? 'PASS' : 'FAIL';
};

export const getPassFailColor = (
  error: number | null,
  hysteresis: number | null,
  tolerance: number
): 'success' | 'error' | 'default' => {
  if (error === null) return 'default';

  const absoluteError = Math.abs(error) <= tolerance;
  const hysteresisPasses = hysteresis === null || hysteresis <= tolerance;

  return absoluteError && hysteresisPasses ? 'success' : 'error';
};
